import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  ViewChild,
} from '@angular/core';
import { Table, TableModule } from 'primeng/table';
import { DatePicker } from 'primeng/datepicker';
import { Button } from 'primeng/button';
import { TableBaseAbstractComponent } from '../../../shared/components/table-base-abstract.component';
import { Regulamin } from '../../../shared/models/regulamin';
import { catchError, EMPTY, filter, finalize, Observable, tap } from 'rxjs';
import { TableResponse } from '../../../shared/models/table-response';
import { TableRequestData } from '../../../shared/models/table/table-request-param';
import { SortDirection } from '../../../shared/enums/sort-direction.enum';
import { DatePipe } from '@angular/common';
import { RulesApi } from '../../../shared/services/api/rules.api';
import { InputText } from 'primeng/inputtext';
import { AppIconComponent } from '../../../shared/components/app-icon/app-icon.component';
import { Paginator } from 'primeng/paginator';
import { Card } from 'primeng/card';
import { PreviewDataWrapperComponent } from '../../../shared/components/preview-data-wrapper/preview-data-wrapper.component';
import { PreviewDataType } from '../../../shared/enums/preview-data-type.enum';
import { PrimeFilterTarget } from '../../../shared/models/custom-types';
import { FormsModule } from '@angular/forms';
import { DialogService } from 'primeng/dynamicdialog';
import { ConfirmDialogComponent } from '../../../shared/components/confirm-dialog/confirm-dialog.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SpinnerService } from '../../../core/services/spinner.service';
import {
  HttpResponseErrorSource,
  HttpResponseService,
} from '../../../core/services/http-error-response.service';
import { Router } from '@angular/router';
import { APP_PATHS } from '../../../core/config/app-paths';
import { TooltipModule } from 'primeng/tooltip';

@Component({
  selector: 'app-admin-panel-rules',
  imports: [
    TableModule,
    DatePicker,
    Button,
    DatePipe,
    InputText,
    AppIconComponent,
    Paginator,
    Card,
    PreviewDataWrapperComponent,
    FormsModule,
    TooltipModule,
  ],
  providers: [RulesApi],
  templateUrl: './admin-panel-rules.html',
  styleUrl: './admin-panel-rules.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminPanelRules extends TableBaseAbstractComponent<Regulamin> {
  @ViewChild('table') override tableRef!: Table;
  protected readonly PreviewDataType = PreviewDataType;
  readonly #rulesApi = inject(RulesApi);
  readonly #dialogService = inject(DialogService);
  readonly #destroyRef = inject(DestroyRef);
  readonly #spinnerService = inject(SpinnerService);
  readonly #httpResponseService = inject(HttpResponseService);
  readonly #router = inject(Router);
  protected tableFilters: Regulamin = {
    id: 0,
    dataUtworzenia: null,
    dataOd: null,
    dataDo: null,
  };

  protected override apiCall$ = (
    params: TableRequestData,
  ): Observable<TableResponse<Regulamin>> => {
    params.sortBy = params.sortBy ?? 'dataPoczatkuObowiazywania';
    params.order = params.order ?? SortDirection.DESC;
    return this.#rulesApi.getRules$(params);
  };

  override onPrimeFilterChangeMobile(
    target: PrimeFilterTarget,
    nazwa: string,
  ): void {
    super.onPrimeFilterChangeMobile(target, nazwa);
  }

  #removeRule(ruleId: number): void {
    this.#spinnerService.show();
    this.#rulesApi
      .deleteRule$(ruleId)
      .pipe(
        tap(() => {
          this.#httpResponseService.showSuccessMessage(
            'Poprawnie usunięto wersję regulaminu.',
          );
          this.reloadData();
        }),
        catchError((err) => {
          this.#httpResponseService.showErrorMessage(
            err,
            'Wystąpił błąd podczas próby usunięcia wersji regulaminu',
            HttpResponseErrorSource.MESSAGE_FROM_RESPONSE,
          );
          return EMPTY;
        }),
        finalize(() => this.#spinnerService.hide()),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe();
  }

  protected openRemoveRuleModal(rule: Regulamin): void {
    const ref = this.#dialogService.open(ConfirmDialogComponent, {
      closable: true,
      header: `Czy na pewno chcesz usunąć wersję regulaminu?`,
      closeOnEscape: true,
      modal: true,
      dismissableMask: true,
    });

    if (ref) {
      ref.onClose
        .pipe(
          filter(Boolean),
          tap(() => this.#removeRule(rule.id)),
          takeUntilDestroyed(this.#destroyRef),
        )
        .subscribe();
    }
  }

  protected openShowRuleDetailsModal(rule: Regulamin): void {
    this.#router.navigate([
      `/${APP_PATHS.admin.root}/${APP_PATHS.admin.rules}/${rule.id}/${APP_PATHS.admin.details}`,
    ]);
  }

  protected onAddNewRuleVersionBtn(): void {
    this.#router.navigate([
      `/${APP_PATHS.admin.root}/${APP_PATHS.admin.rules}/${APP_PATHS.admin.add}`,
    ]);
  }

  protected clearFilters(): void {
    this.tableFilters = {
      id: 0,
      dataUtworzenia: null,
      dataOd: null,
      dataDo: null,
    };
    this.resetFilters();
  }

  protected hideRemoveRuleAction(rule: Regulamin): boolean {
    const today = new Date();
    const inputDate =
      typeof rule.dataOd === 'string' ? new Date(rule.dataOd) : rule.dataOd;
    return inputDate!.getTime() < today.getTime() || !!rule.dataDo;
  }
}
